
import React, { lazy, Suspense, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Hero from '../components/home/Hero';
import TrustSection from '../components/home/TrustSection';
import Navbar from '../components/layout/Navbar';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import WhyChooseUs from '../components/home/WhyChooseUs';
import FinalCTA from '../components/home/FinalCTA';
import ChatbotUI from '../components/chatbot/ChatbotUI';

// Lazy load non-critical components with error handling
const ProcessSection = lazy(() => import('../components/home/ProcessSection').catch(() => {
  console.error('Failed to load ProcessSection');
  return { default: () => <div className="py-10 text-center">Unable to load section</div> };
}));

const Footer = lazy(() => import('../components/layout/Footer').catch(() => {
  console.error('Failed to load Footer');
  return { default: () => <div className="py-4 bg-gray-100 text-center">© ThinkTomorrow.AI</div> };
}));

const FloatingReviewForm = lazy(() => import('../components/reviews/FloatingReviewForm').catch(() => {
  console.error('Failed to load FloatingReviewForm');
  return { default: () => null };
}));

const Index: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Mark component as loaded after initial render
    setIsLoaded(true);
  }, []);

  // Using memo to prevent unnecessary re-renders
  return (
    <>
      <Helmet>
        <title>ThinkTomorrow.AI | AI & Workflow Automation for NZ SMEs</title>
        <meta name="description" content="Practical AI and workflow automation for New Zealand small businesses. Discovery, implementation, training, and governed AI assistants." />
        <link rel="canonical" href="https://thinktomorrow.ai/" />
        <meta property="og:title" content="ThinkTomorrow.AI | AI & Workflow Automation for NZ SMEs" />
        <meta property="og:description" content="Practical AI and workflow automation for New Zealand small businesses." />
        <meta property="og:url" content="https://thinktomorrow.ai/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "ThinkTomorrow.AI",
          "url": "https://thinktomorrow.ai/"
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "ThinkTomorrow.AI",
          "url": "https://thinktomorrow.ai/",
          "logo": "https://thinktomorrow.ai/lovable-uploads/46a89bb9-6390-44d4-8240-2fc3f393f3ba.png",
          "email": "connect@thinktomorrow.ai",
          "telephone": "+64-212-763-763",
          "areaServed": "NZ"
        })}</script>
      </Helmet>
      <Navbar />
      <main>
        <Hero />
        {/* Render TrustSection with key to ensure it only mounts once */}
        <TrustSection key="trust-section" />
        <Suspense fallback={<div className="min-h-[200px] flex items-center justify-center"><LoadingSpinner /></div>}>
          <ProcessSection />
        </Suspense>
        
        {/* Load WhyChooseUs directly instead of lazy loading */}
        <WhyChooseUs />

        {/* Serving Marlborough & Featured Case Study */}
        {isLoaded && (
          <section className="py-16 bg-muted/30">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                {/* Marlborough */}
                <div className="p-6 bg-background rounded-lg border">
                  <h3 className="text-xl font-bold text-foreground mb-3">Serving Marlborough Businesses</h3>
                  <p className="text-muted-foreground text-sm mb-4">Workflow automation for professional services, wine operations, trades, and small businesses in Marlborough and Blenheim.</p>
                  <a href="/workflow-automation-marlborough" className="text-brand-teal hover:underline text-sm font-medium inline-flex items-center gap-1">
                    Learn more →
                  </a>
                </div>
                {/* Featured Case Study */}
                <div className="p-6 bg-background rounded-lg border">
                  <h3 className="text-xl font-bold text-foreground mb-3">Featured Use Case</h3>
                  <p className="text-muted-foreground text-sm mb-4">See how a New Zealand professional services firm used workflow intelligence to reduce coordination overhead and improve project visibility.</p>
                  <a href="/case-studies/architect-workflow-intelligence" className="text-brand-teal hover:underline text-sm font-medium inline-flex items-center gap-1">
                    Read the case study →
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}
        
        {/* Add the Final CTA section */}
        {isLoaded && <FinalCTA />}
      </main>
      <Suspense fallback={<div className="py-4 bg-gray-100 text-center">Loading footer...</div>}>
        <FloatingReviewForm />
        <Footer />
      </Suspense>
      
      {/* Add the Chatbot UI */}
      <ChatbotUI />
    </>
  );
};

export default Index;
